import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import type { TFunction } from 'i18next';
import type { CropUnit } from '../../state/historyStore';
import {
  getPresetById,
  type CropPresetGroup,
  type FlattenedPreset
} from './cropPresets';
import { formatDimension } from '../../utils/cropMath';

interface PresetGroupsPanelProps {
  groups: CropPresetGroup[];
  selectedPresetId: string | null;
  unit: CropUnit;
  onSelectPreset: (presetId: string, preset: FlattenedPreset) => void;
  t: TFunction;
}

type ExpandedState = Record<string, boolean>;

export function PresetGroupsPanel({
  groups,
  onSelectPreset,
  selectedPresetId,
  t,
  unit
}: PresetGroupsPanelProps) {
  const [expandedGroups, setExpandedGroups] = useState<ExpandedState>({});

  useEffect(() => {
    setExpandedGroups((previous) => {
      const next: ExpandedState = {};
      groups.forEach((group) => {
        next[group.id] = previous[group.id] ?? true;
      });
      if (Object.keys(previous).length === Object.keys(next).length) {
        const hasDiff = Object.keys(next).some((key) => previous[key] !== next[key]);
        return hasDiff ? next : previous;
      }
      return next;
    });
  }, [groups]);

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((previous) => ({
      ...previous,
      [groupId]: !(previous[groupId] ?? true)
    }));
  }, []);

  return (
    <div className="space-y-2">
      {groups.map((group) => {
        const isExpanded = expandedGroups[group.id] ?? true;
        return (
          <div key={group.id} className="rounded-2xl border border-ai-surface-glow/10 bg-ai-surface/40">
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left transition hover:border-ai-primary/30 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
              onClick={() => toggleGroup(group.id)}
              aria-expanded={isExpanded}
            >
              <span aria-hidden="true" className="text-lg">
                {group.icon ?? '•'}
              </span>
              <div className="flex flex-1 flex-wrap items-baseline gap-2 text-sm">
                <span className="font-semibold text-ai-text-primary">{t(group.titleKey)}</span>
                {group.descriptionKey ? (
                  <span className="text-xs text-ai-text-secondary">{t(group.descriptionKey)}</span>
                ) : null}
              </div>
              <DisclosureChevron open={isExpanded} />
            </button>
            {isExpanded ? (
              <div className="space-y-2 border-t border-ai-surface-glow/10 p-3 pl-5">
                {group.options.map((option) => (
                  <div
                    key={option.id}
                    className="rounded-2xl border border-ai-surface-glow/10 bg-ai-surface-elevated/30 p-3 shadow-inner"
                  >
                    <div className="flex flex-wrap items-baseline gap-2 text-sm">
                      <span className="font-semibold text-ai-text-primary">{t(option.labelKey)}</span>
                      {option.descriptionKey ? (
                        <span className="text-xs text-ai-text-secondary">{t(option.descriptionKey)}</span>
                      ) : null}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-0">
                      {option.variations?.map((variation) => {
                        const preset = getPresetById(variation.id);
                        if (!preset) {
                          return null;
                        }
                        const isActive = selectedPresetId === variation.id;
                        const variationLabel = t(variation.labelKey);
                        const variationDescription = variation.descriptionKey ? t(variation.descriptionKey) : null;
                        return (
                          <button
                            key={variation.id}
                            type="button"
                            className={clsx(
                              'flex-1 rounded-xl border px-3 py-1 text-xs font-semibold transition focus:outline-none focus-visible:ring focus-visible:ring-ai-focus',
                              isActive
                                ? 'border-ai-primary bg-ai-primary/10 text-ai-primary'
                                : 'border-ai-surface-glow/30 text-ai-text-secondary hover:border-ai-primary/40 hover:text-ai-primary'
                            )}
                            onClick={() => onSelectPreset(variation.id, preset)}
                          >
                            <span className="block">{variationLabel}</span>
                            {variationDescription ? (
                              <span className="block text-ai-text-secondary/80">· {variationDescription}</span>
                            ) : null}
                            <span className="block">
                              · {formatDimension(preset.widthMm, unit)} × {formatDimension(preset.heightMm, unit)}{' '}
                              {t(`history.crop.units.${unit}`)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function DisclosureChevron({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className={clsx('h-3 w-3 text-ai-text-secondary transition-transform duration-200', open ? 'rotate-180' : '')}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
